import SortOrderIcon from '@src/svgs/SortOrderIcon';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { PhotoArtifactTab } from '../../types.ts/story';
import FieldIconButton from '../common/FieldIconButton';
import FileUpload from '../common/FileUpload';
import PanelUploadButton from '../common/PanelUploadButton';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import SearchInput from './SearchInput';

import type { SortBy } from '../../utility/general';

interface PhotoTabActionsProps {
  tab: PhotoArtifactTab;
  sortBy: SortBy;
  onSearch: (searchTerm: string) => void;
  onSort: () => void;
  onUploadCallback: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
}

const PhotoTabActions: React.FC<PhotoTabActionsProps> = ({
  tab,
  sortBy,
  onSearch,
  onSort,
  onUploadCallback,
  setIsLoading,
}) => {
  const renderStockTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Search Photos"
        handleAction={onSearch}
        radius="var(--radius-input)"
        iconRight
      />
    </PhotoTopAction>
  );

  const renderAiTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Describe the photo you want to see and hit enter"
        handleAction={onSearch}
        expandHeight
        hideIcons
        radius="var(--radius-input)"
      />
    </PhotoTopAction>
  );

  // The icon shows the order the list is in now; the label and tooltip say
  // what a click will do. The tooltip used to read "Sort by Upload Date:
  // Oldest" while the list was newest first, which reads as either.
  const newestFirst = sortBy === '-createdAt';
  const sortLabel = newestFirst ? 'Show Oldest First' : 'Show Newest First';

  const renderArtifactTabActions = () => (
    <PhotoTopAction>
      <SearchSortWrapper>
        <SearchInput
          placeholder={
            tab === PhotoArtifactTab.logo ? 'Search Logos' : 'Search Photos'
          }
          handleAction={onSearch}
          radius="var(--radius-input)"
          iconRight
        />
        <FieldIconButton
          onClick={onSort}
          aria-label={sortLabel}
          data-tooltip-id="sort-tooltip"
          data-tooltip-content={sortLabel}
        >
          <SortOrderIcon descending={newestFirst} />
        </FieldIconButton>
        <Tooltip
          id="sort-tooltip"
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />
      </SearchSortWrapper>
      <FileUpload
        width="100%"
        type={(() => {
          if (tab === PhotoArtifactTab.story) return 'storyArtifacts';
          if (tab === PhotoArtifactTab.logo) return 'organizationLogos';
          return 'organizationArtifacts';
        })()}
        Button={
          <PanelUploadButton>
            {tab === PhotoArtifactTab.logo ? 'Upload Logo' : 'Upload Photo'}
          </PanelUploadButton>
        }
        callback={onUploadCallback}
        setIsLoading={setIsLoading}
      />
    </PhotoTopAction>
  );

  switch (tab) {
    case PhotoArtifactTab.stock:
      return renderStockTabActions();
    case PhotoArtifactTab.ai:
      return renderAiTabActions();
    case PhotoArtifactTab.story:
    case PhotoArtifactTab.album:
    case PhotoArtifactTab.logo:
      return renderArtifactTabActions();
    default:
      return null;
  }
};

export default PhotoTabActions;

const PhotoTopAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchSortWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  > form {
    flex: 1;
  }
`;
